import MenuIcon from '@mui/icons-material/Menu';
import { Alert, Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth';
import {
  EmbeddedError,
  EmbeddedFrame,
  EmbeddedLoading,
  EmbeddedNavigation,
  EmbeddedNavigationSkeleton,
  EmbeddedStatusBar,
} from '@/components/embedded';
import { useEmbeddedApplication } from '@/hooks/useEmbeddedApplication';
import type {
  EmbeddedApplication,
  EmbeddedNavigationUrlResolutionError,
  EmbeddedNavigationItem,
} from '@/types/embeddedApplication';

function getIframeResolutionWarning(
  resolutionError: EmbeddedNavigationUrlResolutionError | undefined,
  t: (key: string) => string,
): string | null {
  if (!resolutionError) {
    return null;
  }

  if (resolutionError === 'invalidApplicationBaseUrl') {
    return t('embedded.embeddedIframeUrlInvalid');
  }

  return t('navigationConfig.menuRouteUrlInvalid');
}

interface EmbeddedApplicationLayoutProps {
  application: EmbeddedApplication;
  selectedNavigationItemId?: string;
  onNavigationItemChange?: (
    itemId: string,
    item: EmbeddedNavigationItem,
  ) => void;
  onPopOut?: (url: string) => void;
  onPopOutActionReady?: (trigger: (() => void) | null) => void;
  isNavigationLoading?: boolean;
  navigationError?: string;
}

export const EmbeddedApplicationLayout = memo(
  function EmbeddedApplicationLayout({
    application,
    selectedNavigationItemId,
    onNavigationItemChange,
    onPopOut,
    onPopOutActionReady,
    isNavigationLoading = false,
    navigationError,
  }: EmbeddedApplicationLayoutProps) {
    const { t } = useTranslation('entity-portal');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
    const useDrawerNavigation = isMobile || isTablet;
    const { user } = useAuth();

    const {
      iframeRef,
      selectedItemId,
      filteredNavigation,
      expandedGroups,
      navigationQuery,
      setNavigationQuery,
      sidebarOpen,
      setSidebarOpen,
      toggleGroup,
      selectNavigationItem,
      resolvedIframeUrl,
      iframeUrlResolutionError,
      hasInvalidIframeUrl,
      reloadIframe,
      popOut,
      reloadToken,
      isLoading,
      hasError,
      handleIframeLoad,
      handleIframeError,
    } = useEmbeddedApplication({
      application,
      selectedNavigationItemId,
      userRoles: user?.roles,
      onNavigationItemChange,
      onPopOut,
    });

    useEffect(() => {
      onPopOutActionReady?.(popOut);

      return () => {
        onPopOutActionReady?.(null);
      };
    }, [onPopOutActionReady, popOut]);

    const selectedNavItem = useMemo(
      () =>
        filteredNavigation
          .flatMap((group) => group.items)
          .find((item) => item.id === selectedItemId),
      [filteredNavigation, selectedItemId],
    );

    const frameTitle = selectedNavItem?.label ?? application.name;

    const hasNavigation = application.navigation.length > 0;
    const isNavigationPending = isNavigationLoading && !hasNavigation;
    const showNavigationColumn = hasNavigation || isNavigationLoading;
    const iframeResolutionWarning = getIframeResolutionWarning(
      iframeUrlResolutionError,
      t,
    );

    return (
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          height: '100%',
          bgcolor: 'background.default',
        }}
      >
        {showNavigationColumn ? (
          <>
            {!useDrawerNavigation ? (
              isNavigationPending ? (
                <EmbeddedNavigationSkeleton />
              ) : (
                <EmbeddedNavigation
                  groups={filteredNavigation}
                  selectedItemId={selectedItemId}
                  expandedGroups={expandedGroups}
                  navigationQuery={navigationQuery}
                  onNavigationQueryChange={setNavigationQuery}
                  onToggleGroup={toggleGroup}
                  onSelectItem={selectNavigationItem}
                />
              )
            ) : isNavigationPending && sidebarOpen ? (
              <EmbeddedNavigationSkeleton />
            ) : (
              <EmbeddedNavigation
                groups={filteredNavigation}
                selectedItemId={selectedItemId}
                expandedGroups={expandedGroups}
                navigationQuery={navigationQuery}
                onNavigationQueryChange={setNavigationQuery}
                onToggleGroup={toggleGroup}
                onSelectItem={selectNavigationItem}
                mobileOpen={sidebarOpen}
                onMobileClose={() => setSidebarOpen(false)}
              />
            )}
          </>
        ) : null}

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {useDrawerNavigation && showNavigationColumn ? (
            <Box
              sx={{
                px: 2,
                py: 1,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <IconButton
                size="small"
                aria-label={t('embedded.embeddedOpenNavigation')}
                disabled={isNavigationPending}
                onClick={() => setSidebarOpen(true)}
              >
                <MenuIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : null}

          {navigationError ? (
            <Box sx={{ px: 2, py: 1 }}>
              <Alert severity="error">{navigationError}</Alert>
            </Box>
          ) : null}

          {iframeResolutionWarning ? (
            <Box sx={{ px: 2, py: 1 }}>
              <Alert severity="warning">{iframeResolutionWarning}</Alert>
            </Box>
          ) : null}

          <Box
            sx={{
              position: 'relative',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            {hasInvalidIframeUrl ? (
              <Box sx={{ p: 3 }}>
                <Alert severity="warning">
                  {t('product.iframeSelfEmbedError')}
                </Alert>
              </Box>
            ) : (
              <>
                <EmbeddedFrame
                  ref={iframeRef}
                  application={application}
                  src={resolvedIframeUrl}
                  reloadToken={reloadToken}
                  title={frameTitle}
                  onLoad={handleIframeLoad}
                  onError={handleIframeError}
                />

                {isLoading || isNavigationLoading ? (
                  <EmbeddedLoading applicationName={application.name} />
                ) : null}

                {hasError ? (
                  <EmbeddedError
                    applicationName={application.name}
                    onRetry={reloadIframe}
                    onReload={reloadIframe}
                    onOpenInNewTab={popOut}
                  />
                ) : null}

                {application.status ? (
                  <EmbeddedStatusBar status={application.status} />
                ) : null}
              </>
            )}
          </Box>
        </Box>
      </Box>
    );
  },
);
